import { ChangeDetectorRef, Component,  Input, OnInit, ViewChild } from '@angular/core';
import { PageHandle, PAGE_NAME } from 'globals/store/permissions.data';
import { filterPageTypes, toggleFilterTypes, FilterType } from 'src/app/store/filters.era';
import { traceNames } from '../../services/performance.tracking.data';
import { readOnlyReselector } from 'src/app/store/reselectors/permissions.reselector';
import { assetColumns, AssetTableData, PerformerTableData } from 'src/app/helpers/table-column-types';
import { TableColumn, TableMenuItem } from 'src/app/helpers/table.data';
import { assetColumnsById } from 'src/app/helpers/table-helpers';
import { loadLocal } from 'src/app/helpers/helpers';
import { TaskTimeTableData } from '../../helpers/table-column-types';
import { openDialogGetResult } from 'src/app/helpers/angular-helpers';
import { Wrapper } from 'src/app/components/wrapper.component';
import { JoiEvent, LabelObject } from 'globals/store/events.data';
import { ManageAssetComponent, ManageAssetData } from './manage-asset.component';
import { addAnAsset, chooseOrAddEntityTypes, ChooseOrAddTypesData, saveTable } from '../performers/performerSharedFunctions';
import { ConfirmDeleteData, openConfirmDeleteGetResult } from '../../components/dialog.confirmDelete.component';
import { assetColumnsReselector, assetTableReselector } from '../../store/reselectors/assets.reselector';
import { NestedTableComponent, TableIconEmitterData } from '../../components/nested-table/nested-table.component';
import { Asset } from 'globals/store/assets.data';
import { AssetsService } from '../../services/assets.service';
import { LoaderPaneFragmentComponent } from 'src/app/components/loader/loader-pane-fragment.component';
import { TopMenuComponent } from '../program/top.menu';
import { MobileMenuComponent } from 'src/app/components/menu/mobile-menu.component';
import { DatePipe } from '@angular/common';
import { CommandMenuComponent } from 'src/app/components/nav-bar/command-menu.component';

@Component({
    selector: 'asset-table',
    template: `
  <top-menu></top-menu>
  <mobile-menu></mobile-menu>
  <command-menu
    [filterPage]="filterPage"
    [filterText]="'Filter assets'"
    [showDottedMenu]="true"
    [canEditColumns]="true"
    [showExport]="true"
    [addItemText]="!event || event.is_readonly ? '' : '+ ADD AN ASSET'"
    (addItem)="addAnAsset($event)"
    (onExport)="saveTable()"
  ></command-menu>
  <nested-table
    [items]="tableData"
    [filtersId]="filterPage"
    [condensedView]="condensed"
    [columns]="columnData"
    [columnsById]="columnsById"
    [menuItems]="menuItems"
    [menuDisabled]="busy"
    [defaultSelectedColumnId]="assetColumns.name"
    [readOnly]="!!event?.is_readonly"
    [emptyMessage]="'No assets in this event'"
    (selectItem)="manageAsset($event)"
    (iconClick)="toggleIcon($event)"
    (updatedSelectedIds)="selectIds($event)"
  ></nested-table>
  <loader-pane-fragment [trace]="traceNames.viewAssetsTable"></loader-pane-fragment>
  `,
    styleUrls: ['../../page.scss'],
    standalone: true,
    providers: [DatePipe],
    imports: [NestedTableComponent, CommandMenuComponent, LoaderPaneFragmentComponent, TopMenuComponent, MobileMenuComponent, ]
})
export class AssetTableComponent extends Wrapper<AssetTableComponent> implements OnInit {

  @Input() isMyTasks:boolean = false
  @Input() page_name: PageHandle = PAGE_NAME.asset_table
  @Input() filterPage: FilterType = filterPageTypes.asset_table
  @Input() traceName = traceNames.viewTasksTime
  traceNames = traceNames
  readOnly=true
  
  columnData: TableColumn[] = []
  columnsById: { [id: string]: TableColumn } = assetColumnsById
  tableRowsById: { [id:string]: AssetTableData } = {}
  tableData: AssetTableData[] = []
  allMenuItems: TableMenuItem[] = [
    { title: 'Delete', method: this.deleteAssets.bind(this) },
    { title: 'Change category', method: this.changeAssetCategory.bind(this) }
  ]
  menuItems: TableMenuItem[] = this.allMenuItems // default to showing all menu items
  assetColumns = assetColumns

  condensed: boolean
  busy = false
  
  event: JoiEvent
  asset_labels: LabelObject = {}
  selectedAssetIds: string[]

  taskTimeDataRows: TaskTimeTableData[]
  @ViewChild(NestedTableComponent) nestedTable: NestedTableComponent<PerformerTableData>
  // taskTimeDataRowsById: { [id:string]: TaskTimeTableData }


  constructor(private changeDetectorRef: ChangeDetectorRef) {
    super()
    this.uiService.changePageTitle("Sponsors", true)
    this.subProp(this.store.select('events', 'event'), 'event')
    this.subProp(this.store.select('events', 'labelMaps', 'asset_labels'), 'asset_labels')
    this.subCallback(this.store.select(assetTableReselector), data => {
      if (!data) {
        this.tableData = [] // for when the last is deleted
        return
      }
      const { assetTableData, assetTableRowsById } = data
      this.tableData = assetTableData
      this.tableRowsById = assetTableRowsById
    })
    this.loadCondensed()
    this.subCallback(this.store.select('filters', 'toggleFilters', this.filterPage, 'condensed'), _ => this.loadCondensed())
  }

  ngOnInit() {
    this.subProp(this.store.select(readOnlyReselector), 'readOnly')
    this.loadCondensed()
    this.subCallback(this.store.select('filters', 'toggleFilters', this.filterPage, 'condensed'), _ => this.loadCondensed())
    this.subProp(this.store.select(assetColumnsReselector), 'columnData')
  }

  saveTable() {
    saveTable('assets' ,`${this.event.title} assets table`, this.dialog, this.nestedTable)
  }

  toggleIcon(d: TableIconEmitterData<AssetTableData>) {
    const assetRow = d.row
    switch (d.iconType) {
      case 'flag':
        const asset = assetRow.asset
        asset.flagged = !asset.flagged
        this.assetService.upsertAsset(asset)
        break
      default:
        console.log(`Unhandled icon type: ${d.iconType}`)
        break
    }
  }

  async deleteAssets(toDelete: AssetTableData[], event) {
    const data: ConfirmDeleteData = {
      title: `Delete ${toDelete.length} assets`,
      rect: event.target.getBoundingClientRect(),
      para: `Are you sure you want to delete ${toDelete.length > 1 ? `all ${toDelete.length} selected assets` : 'the selected asset'}? The asset${toDelete.length > 1 ? 's' : ''} will also be removed from the event.`,
      dialog: this.dialog
    }
    const del = await openConfirmDeleteGetResult(data)
    if (!del) return
    this.assetService.deleteAssets(toDelete.map(d => d.asset.id))
  }

  addAnAsset(event) {
    addAnAsset(event, this.dialog, this.assetService, this.manageAsset.bind(this))
  }

  loadCondensed() {
    this.condensed = loadLocal(`${this.filterPage}:${toggleFilterTypes.condensed}`, false)
  } 

  async changeAssetCategory(toChange: AssetTableData[], event) {
    const parent = event.target.parentElement
    const rect = parent.getBoundingClientRect()

    const position = { top: rect.bottom - 2, left: rect.left }
    const data: ChooseOrAddTypesData<AssetsService, Asset> = {
      position, 
      dialog: this.dialog, 
      entityService: this.assetService, 
      types: this.event.assetCategories,
      entities: toChange.map(({ asset }) => asset),
      single: false,
      typeName: 'Asset',
      isCategory: true
    }
    console.log(data)
    await chooseOrAddEntityTypes(data)
  }
  

  async manageAsset({ id }: AssetTableData) {
    console.log({ id })
    const data: ManageAssetData = { assetId: id }
    this.changeDetectorRef.detach()
    await openDialogGetResult(this.dialog, ManageAssetComponent, { disableClose: true, autoFocus: false, data, panelClass: 'full-screen-modal' })
    this.changeDetectorRef.reattach()
  }

  selectIds(ids: Set<string>) {
    this.selectedAssetIds = Array.from(ids)
    // console.log(this.selectedTaskIds)
    setTimeout(() => this.buildMenu(false), 0)
  }

  private get selectedAssets(): Asset[] {
    return (this.selectedAssetIds || []).map(id => this.tableRowsById[id].asset)
  }

  buildMenu(disableMenuTemporarily = true) {
    const assets = this.selectedAssets
    const nAssets = assets.length

    if (nAssets === 0) return
    
    const nLinked = assets.filter(a => a.showInBudget).length
    this.menuItems = [... this.allMenuItems]

    if (nLinked < nAssets) this.menuItems.push({ title: 'Link to budget', method: this.linkToBudget.bind(this) })
    if (nLinked) this.menuItems.push({ title: 'Unlink from budget', method: this.unlinkFromBudget.bind(this) })   
  }

  async linkToBudget(rows: AssetTableData[], event) {
    const toModify = this.selectedAssets.filter(a => !a.showInBudget)
    toModify.forEach(a => a.showInBudget = true)
    await this.assetService.upsertAssets(toModify)
    this.buildMenu()
  }

  async unlinkFromBudget(rows: AssetTableData[], event) {
    const toModify = this.selectedAssets.filter(a => a.showInBudget)
    toModify.forEach(a => a.showInBudget = false)
    await this.assetService.upsertAssets(toModify)
    this.buildMenu()
  }

}

